import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ReportIncidenService from "../../../components/api/ReportIncidenService";
import getApiErrorMessage from "../../../utils/getApiErrorMessage";
import { getDsrDeadlineHistoryQueryKey } from "../../deadline-history/hooks/useDsrDeadlineHistory";
import { getDsrDeadlineWorkflowQueryKey } from "./useDsrDeadlineWorkflow";

export const useDsrDeadlineActions = (caseId, t) => {
  const queryClient = useQueryClient();

  const replaceWorkflow = async (workflow) => {
    const workflowKey = getDsrDeadlineWorkflowQueryKey(caseId);

    await queryClient.cancelQueries({ queryKey: workflowKey, exact: true });

    if (
      workflow &&
      typeof workflow === "object" &&
      ("is_awaiting_requester" in workflow || workflow.dsr_deadline_at)
    ) {
      queryClient.setQueryData(workflowKey, workflow);
    }

    await queryClient.refetchQueries({ queryKey: workflowKey, exact: true });
    queryClient.invalidateQueries({
      queryKey: getDsrDeadlineHistoryQueryKey(caseId),
      exact: true,
    });
  };

  const startWaitingPeriod = useMutation({
    mutationFn: async ({ reason, startedAt }) => {
      const service = ReportIncidenService.getInstance();
      return await service.startAwaitingRequester(caseId, {
        startedAt,
        reason,
      });
    },
    onSuccess: (workflow) => {
      replaceWorkflow(workflow);
      toast(t("Waiting period started."), { type: "success" });
    },
    onError: (error) => {
      toast(
        t(getApiErrorMessage(error, t("Failed to start the waiting period."))),
        { type: "error" }
      );
    },
  });

  const resumeWorkflow = useMutation({
    mutationFn: async () => {
      const service = ReportIncidenService.getInstance();
      return await service.resumeAwaitingRequester(caseId);
    },
    onSuccess: (workflow) => {
      replaceWorkflow(workflow);
      toast(t("Workflow resumed."), { type: "success" });
    },
    onError: (error) => {
      toast(
        t(getApiErrorMessage(error, t("Failed to resume the workflow."))),
        { type: "error" }
      );
    },
  });

  return {
    startWaitingPeriod,
    resumeWorkflow,
  };
};
